import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type RefObject,
} from 'react';
import {
  handleDropdownFilterKeyDown,
  handleDropdownTriggerKeyDown,
} from './liksDropdownTriggerKeyboard';
import {
  getDropdownPanelStyle,
  scrollDropdownActiveOptionIntoPanel,
} from './liksDropdownPanelPosition';
import type { LiksDropdownOption } from './LiksDropdown.types';

type UseLiksDropdownStateOptions = {
  value: string;
  onChange: (value: string) => void;
  options: LiksDropdownOption[];
  disabled: boolean;
  filterable: boolean;
};

export type UseLiksDropdownStateResult = {
  rootRef: RefObject<HTMLDivElement | null>;
  triggerRef: RefObject<HTMLButtonElement | null>;
  panelRef: RefObject<HTMLDivElement | null>;
  optionsListRef: RefObject<HTMLDivElement | null>;
  filterInputRef: RefObject<HTMLInputElement | null>;
  optionRefs: RefObject<Array<HTMLDivElement | null>>;
  isOpen: boolean;
  activeIndex: number;
  panelStyle: CSSProperties;
  filterQuery: string;
  setFilterQuery: (query: string) => void;
  visibleOptions: LiksDropdownOption[];
  selectedOption: LiksDropdownOption | undefined;
  selectedLabel: string;
  activeOptionId: string | undefined;
  open: () => void;
  close: () => void;
  selectIndex: (index: number) => void;
  setActiveIndex: (index: number) => void;
  handleTriggerKeyDown: (event: KeyboardEvent<HTMLButtonElement>) => void;
  handleFilterKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
};

export function useLiksDropdownState({
  value,
  onChange,
  options,
  disabled,
  filterable,
  listboxId,
  placeholder,
}: UseLiksDropdownStateOptions & {
  listboxId: string;
  placeholder?: string;
}): UseLiksDropdownStateResult {
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  const filterInputRef = useRef<HTMLInputElement>(null);
  const optionRefs = useRef<Array<HTMLDivElement | null>>([]);
  const typeaheadRef = useRef({ query: '', timeoutId: 0 });

  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelStyle, setPanelStyle] = useState<CSSProperties>({});
  const [filterQuery, setFilterQuery] = useState('');

  const visibleOptions = useMemo(() => {
    if (!filterable) {
      return options;
    }
    const query = filterQuery.trim().toLowerCase();
    if (query === '') {
      return options;
    }
    return options.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [filterQuery, filterable, options]);

  const selectedIndex = options.findIndex((option) => option.value === value);
  const selectedOption =
    selectedIndex >= 0 ? options[selectedIndex] : undefined;
  const selectedLabel = selectedOption?.label ?? placeholder ?? '';
  const activeOption = visibleOptions[activeIndex];
  const activeOptionId =
    activeOption !== undefined
      ? `${listboxId}-option-${activeIndex}`
      : undefined;

  const getPanelStyle = useCallback(
    (): CSSProperties | null => getDropdownPanelStyle(triggerRef, panelRef),
    [],
  );

  const updatePanelPosition = useCallback(() => {
    const nextStyle = getPanelStyle();
    if (nextStyle) {
      setPanelStyle(nextStyle);
    }
  }, [getPanelStyle]);

  const scrollActiveOptionIntoPanel = useCallback((index: number) => {
    scrollDropdownActiveOptionIntoPanel(
      optionsListRef,
      panelRef,
      optionRefs.current[index],
    );
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setFilterQuery('');
  }, []);

  const open = useCallback(() => {
    if (disabled || options.length === 0) {
      return;
    }
    const nextStyle = getPanelStyle();
    if (nextStyle) {
      setPanelStyle(nextStyle);
    }
    const selectedVisibleIndex = options.findIndex(
      (option) => option.value === value,
    );
    setActiveIndex(selectedVisibleIndex >= 0 ? selectedVisibleIndex : 0);
    setFilterQuery('');
    setIsOpen(true);
  }, [disabled, getPanelStyle, options, value]);

  const selectIndex = useCallback(
    (index: number) => {
      const option = visibleOptions[index];
      if (!option) {
        return;
      }
      if (option.value !== value) {
        onChange(option.value);
      }
      close();
      triggerRef.current?.focus();
    },
    [close, onChange, value, visibleOptions],
  );

  const moveActiveIndex = useCallback(
    (nextIndex: number) => {
      if (visibleOptions.length === 0) {
        return;
      }
      setActiveIndex(
        Math.min(Math.max(nextIndex, 0), visibleOptions.length - 1),
      );
    },
    [visibleOptions.length],
  );

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) {
        return;
      }
      close();
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [close, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    // Refine after mount (panel size known); avoid sync setState in effect.
    const frame = window.requestAnimationFrame(() => {
      updatePanelPosition();
      if (filterable) {
        filterInputRef.current?.focus();
      }
    });
    const handleReposition = () => updatePanelPosition();
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [filterable, isOpen, visibleOptions, updatePanelPosition]);

  useEffect(() => {
    if (isOpen) {
      scrollActiveOptionIntoPanel(activeIndex);
    }
  }, [activeIndex, isOpen, scrollActiveOptionIntoPanel]);

  useEffect(() => {
    const typeahead = typeaheadRef.current;
    return () => window.clearTimeout(typeahead.timeoutId);
  }, []);

  const handleTriggerKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    handleDropdownTriggerKeyDown({
      event,
      disabled,
      optionsLength: options.length,
      filterable,
      isOpen,
      activeIndex,
      selectedIndex,
      visibleOptions,
      typeaheadRef,
      open,
      close,
      moveActiveIndex,
      selectIndex,
      setActiveIndex,
      focusTrigger: () => triggerRef.current?.focus(),
    });
  };

  const handleFilterKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    handleDropdownFilterKeyDown({
      event,
      isOpen,
      activeIndex,
      visibleOptionsLength: visibleOptions.length,
      open,
      close,
      moveActiveIndex,
      selectIndex,
      focusTrigger: () => triggerRef.current?.focus(),
    });
  };

  return {
    rootRef,
    triggerRef,
    panelRef,
    optionsListRef,
    filterInputRef,
    optionRefs,
    isOpen,
    activeIndex,
    panelStyle,
    filterQuery,
    setFilterQuery,
    visibleOptions,
    selectedOption,
    selectedLabel,
    activeOptionId,
    open,
    close,
    selectIndex,
    setActiveIndex,
    handleTriggerKeyDown,
    handleFilterKeyDown,
  };
}
